import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  IMAP_PRESETS, MAILBOX_POLL_INTERVALS, MAILBOX_PROVIDER_LABEL, inboundMailboxTarget, mailboxOutboundProvider,
  type ImapPresetKey, type ImapSecurity, type InboundMailboxInput, type InboundMailboxSettings, type InboundMailboxTestResult, type InboundMailboxView, type MailboxProvider, type MailboxSyncResult,
} from '@pqrs/contracts';
import { Icono } from '@xiltrion/ui/icono';
import { Alert, AlertDescription, AlertTitle } from '@xiltrion/ui/alert';
import { Badge } from '@xiltrion/ui/badge';
import { Button } from '@xiltrion/ui/button';
import { Checkbox } from '@xiltrion/ui/checkbox';
import { CargaArchivos } from '@xiltrion/ui/bloques/carga-archivos';
import { ConfirmarDialogo } from '@xiltrion/ui/bloques/confirmar';
import { Plegable } from '@xiltrion/ui/bloques/plegable';
import { SelectorSimple } from '@xiltrion/ui/bloques/selectores';
import { Input } from '@xiltrion/ui/input';
import { Label } from '@xiltrion/ui/label';
import { Spinner } from '@xiltrion/ui/spinner';
import { Switch } from '@xiltrion/ui/switch';
import { apiDelete, apiGet, apiPost, apiPut } from '@/api/client';
import { Card, ErrorState, KeyValue, LoadingState, fmtDate } from '@/components/ui';
import { errorMessage } from '@/features/cases/lib';
import { useSession } from '@/app/session';
import { SETUP_QUERY_KEY } from '@/features/setup/api';
import { ProviderRadio, SecretField, isEmail, readServiceAccountJson, type ProviderOption } from './email-shared';
import { qk } from './api';
import { ConfiguredService, Field, notify, notifyError } from './shared';

const KEY = ['config', 'email', 'mailbox'] as const;
export const useInboundMailbox = (enabled = true) => useQuery({ queryKey: KEY, queryFn: () => apiGet<InboundMailboxView>('/communications/inbound/mailbox'), enabled, refetchInterval: (q) => (q.state.data?.source === 'tenant' ? 30_000 : false) });

const PROVIDERS: Array<ProviderOption<MailboxProvider>> = [
  { value: 'MICROSOFT_365', label: MAILBOX_PROVIDER_LABEL.MICROSOFT_365, icon: 'mdi:microsoft', description: 'Buzón de Exchange Online, con una aplicación registrada en Microsoft Entra.' },
  { value: 'GOOGLE_WORKSPACE', label: MAILBOX_PROVIDER_LABEL.GOOGLE_WORKSPACE, icon: 'mdi:google', description: 'Buzón de Gmail de la entidad, con una cuenta de servicio y delegación del dominio.' },
  { value: 'IMAP', label: MAILBOX_PROVIDER_LABEL.IMAP, icon: 'mdi:email-sync-outline', description: 'Una cuenta normal de Gmail, Zoho, Yahoo o el servidor propio, con usuario y contraseña de aplicación.' },
];
const SECURITY_OPTIONS: Array<{ value: ImapSecurity; label: string }> = [
  { value: 'tls', label: 'TLS directo (993)' },
  { value: 'starttls', label: 'STARTTLS (143)' },
  { value: 'none', label: 'Sin cifrado (solo red interna)' },
];
const SECRET_LABEL: Record<MailboxProvider, string> = { MICROSOFT_365: 'Secreto del cliente', GOOGLE_WORKSPACE: 'Llave privada', IMAP: 'Contraseña' };
const INTERVAL_OPTIONS = MAILBOX_POLL_INTERVALS.map((s) => ({ value: String(s), label: s < 60 ? `${s} segundos` : s === 60 ? 'Cada minuto' : `Cada ${s / 60} minutos` }));

interface Form {
  provider: MailboxProvider;
  useOutbound: boolean;
  preset: ImapPresetKey;
  tenantId: string;
  clientId: string;
  serviceAccountEmail: string;
  host: string;
  port: string;
  security: ImapSecurity;
  user: string;
  folder: string;
  mailbox: string;
  processedFolder: string;
  pollIntervalSeconds: string;
  /** Secreto nuevo (vacío = se conserva el guardado). */
  secret: string;
  clearSecret: boolean;
  keyFile: string | null;
}

function initialForm(s: InboundMailboxSettings | null): Form {
  const base: Form = { provider: 'MICROSOFT_365', useOutbound: false, preset: 'custom', tenantId: '', clientId: '', serviceAccountEmail: '', host: '', port: '993', security: 'tls', user: '', folder: 'INBOX', mailbox: '', processedFolder: '', pollIntervalSeconds: '60', secret: '', clearSecret: false, keyFile: null };
  if (!s) return base;
  const common = { ...base, provider: s.provider, mailbox: s.mailbox, processedFolder: s.processedFolder ?? '', pollIntervalSeconds: String(s.pollIntervalSeconds) };
  switch (s.provider) {
    case 'MICROSOFT_365': return { ...common, useOutbound: s.credentials === 'outbound', tenantId: s.tenantId ?? '', clientId: s.clientId ?? '' };
    case 'GOOGLE_WORKSPACE': return { ...common, useOutbound: s.credentials === 'outbound', serviceAccountEmail: s.serviceAccountEmail ?? '' };
    case 'IMAP': return { ...common, preset: s.preset, host: s.host, port: String(s.port), security: s.security, user: s.user, folder: s.folder };
  }
}

const orNull = (v: string) => v.trim() || null;

/** Lo visible del formulario, con la misma forma que lo guardado (para saber si el secreto guardado aplica). */
function settingsOf(f: Form): InboundMailboxSettings {
  const common = { mailbox: f.mailbox.trim().toLowerCase(), processedFolder: orNull(f.processedFolder), pollIntervalSeconds: Number(f.pollIntervalSeconds) || 60 };
  const credentials = f.useOutbound ? ('outbound' as const) : ('own' as const);
  switch (f.provider) {
    case 'MICROSOFT_365': return { provider: 'MICROSOFT_365', credentials, tenantId: orNull(f.tenantId), clientId: orNull(f.clientId), ...common };
    case 'GOOGLE_WORKSPACE': return { provider: 'GOOGLE_WORKSPACE', credentials, serviceAccountEmail: orNull(f.serviceAccountEmail), ...common };
    case 'IMAP': return { provider: 'IMAP', preset: f.preset, host: f.host.trim(), port: Number(f.port), security: f.security, user: f.user.trim(), folder: f.folder.trim() || 'INBOX', ...common };
  }
}

/** Sin secreto escrito no se manda el campo (se conserva el guardado); con «quitar», `null`. */
function bodyOf(f: Form): InboundMailboxInput {
  const secretField = { MICROSOFT_365: 'clientSecret', GOOGLE_WORKSPACE: 'privateKey', IMAP: 'password' }[f.provider];
  const secret = f.useOutbound ? {} : f.secret ? { [secretField]: f.secret } : f.clearSecret ? { [secretField]: null } : {};
  return { ...settingsOf(f), ...secret } as InboundMailboxInput;
}

/**
 * Buzón de correo entrante de la entidad. Configurarlo o cambiarlo es de «Administrar configuración avanzada» (el API
 * lo exige); el Administrador ve qué buzón hay y a quién pedir el cambio.
 */
export function InboundMailboxCard() {
  return useSession().can('admin.advanced') ? <InboundMailboxEditor /> : <InboundMailboxReadOnly />;
}

/** Sin configuración avanzada: qué buzón se lee (dirección y servicio), cómo va la lectura y que el cambio lo hace soporte. */
function InboundMailboxReadOnly() {
  const q = useInboundMailbox();
  const title = 'Buzón de correo entrante';
  if (q.isLoading) return <Card title={title}><LoadingState rows={2} /></Card>;
  if (q.isError || !q.data) return <Card title={title}><ErrorState error={q.error} onRetry={() => q.refetch()} /></Card>;
  const v = q.data;
  const saved = v.source === 'tenant' ? v.settings : null;
  const sync$ = v.sync;
  const failing = !!sync$?.lastError && (!sync$.lastSuccessAt || (sync$.lastErrorAt ?? '') > sync$.lastSuccessAt);
  if (!saved) {
    return (
      <Card title={title}>
        <Alert variant="warning">
          <Icono nombre="mdi:email-off-outline" />
          <AlertTitle>La entidad no tiene buzón de correo entrante</AlertTitle>
          <AlertDescription>Las solicitudes que llegan por correo no entran solas a la plataforma. Para configurarlo, comunícate con soporte.</AlertDescription>
        </Alert>
      </Card>
    );
  }
  return (
    <Card title={title}>
      <div className="flex flex-col gap-3">
        <ConfiguredService icon={PROVIDERS.find((p) => p.value === saved.provider)?.icon ?? 'mdi:email-outline'} title={saved.mailbox}
          items={[
            { label: 'Plataforma', value: MAILBOX_PROVIDER_LABEL[saved.provider] },
            { label: 'Última revisión', value: sync$?.lastSyncAt ? fmtDate(sync$.lastSyncAt) : 'Todavía no se ha revisado' },
          ]} actions={null} />
        {failing && <Alert variant="destructive"><Icono nombre="mdi:alert-circle-outline" /><AlertTitle>La última lectura del buzón falló</AlertTitle><AlertDescription>Comunícate con soporte para revisarlo.</AlertDescription></Alert>}
        <Alert variant="info"><Icono nombre="mdi:lifebuoy" /><AlertDescription>Para cambiar el buzón o la plataforma de correo, comunícate con soporte.</AlertDescription></Alert>
      </div>
    </Card>
  );
}

function InboundMailboxEditor() {
  const qc = useQueryClient();
  const q = useInboundMailbox();
  const [f, setF] = useState<Form>(() => initialForm(null));
  const [removing, setRemoving] = useState(false);
  /** Con buzón guardado se muestra el resumen; el formulario solo al editar. */
  const [editing, setEditing] = useState(false);
  const [keyFiles, setKeyFiles] = useState<File[]>([]);
  const [keyError, setKeyError] = useState<string | null>(null);
  const v = q.data;
  useEffect(() => { if (v) setF(initialForm(v.settings)); }, [v?.updatedAt, v?.source]); // eslint-disable-line react-hooks/exhaustive-deps
  const summary = v?.source === 'tenant' && !editing;

  const set = (patch: Partial<Form>) => { setF((x) => ({ ...x, ...patch })); test.reset(); };
  const saved = v?.settings ?? null;
  const storedSecretApplies = useMemo(() => {
    if (!saved?.hasSecret || saved.provider !== f.provider || f.useOutbound) return false;
    try { return inboundMailboxTarget(saved) === inboundMailboxTarget(settingsOf(f)); } catch { return false; }
  }, [saved, f]);
  const hasSecret = !!f.secret || (storedSecretApplies && !f.clearSecret);
  /** El correo saliente guardado es del mismo servicio: puede prestar sus credenciales. */
  const canUseOutbound = !!v?.outboundProvider && v.outboundProvider === mailboxOutboundProvider(f.provider);

  const missing = (() => {
    if (!isEmail(f.mailbox)) return 'la dirección del buzón';
    if (f.provider === 'IMAP') {
      if (!f.host.trim()) return 'el servidor';
      if (!f.port || Number.isNaN(Number(f.port))) return 'el puerto';
      if (!f.user.trim()) return 'el usuario';
      if (!hasSecret) return 'la contraseña';
      return null;
    }
    if (f.useOutbound) return canUseOutbound ? null : 'el correo saliente del mismo servicio (o credenciales propias)';
    if (f.provider === 'MICROSOFT_365' && (!f.tenantId.trim() || !f.clientId.trim())) return 'los datos de la aplicación';
    if (f.provider === 'GOOGLE_WORKSPACE' && !isEmail(f.serviceAccountEmail)) return 'la cuenta de servicio';
    if (!hasSecret) return SECRET_LABEL[f.provider].toLowerCase();
    return null;
  })();

  const onSaved = (nv: InboundMailboxView, msg: string) => { qc.setQueryData(KEY, nv); qc.invalidateQueries({ queryKey: SETUP_QUERY_KEY }); qc.invalidateQueries({ queryKey: qk.settings }); notify('success', msg); };
  const save = useMutation({ mutationFn: () => apiPut<InboundMailboxView>('/communications/inbound/mailbox', bodyOf(f)), onSuccess: (nv) => { onSaved(nv, 'Buzón de correo entrante guardado'); setEditing(false); }, onError: notifyError });
  const remove = useMutation({ mutationFn: () => apiDelete<InboundMailboxView>('/communications/inbound/mailbox'), onSuccess: (nv) => { onSaved(nv, 'Buzón de correo entrante quitado'); setEditing(false); }, onError: notifyError });
  /** En el resumen se prueba lo guardado (sin mandar configuración); al editar, lo del formulario. */
  const test = useMutation({ mutationFn: () => apiPost<InboundMailboxTestResult>('/communications/inbound/mailbox/test', summary ? {} : { config: bodyOf(f) }) });
  const sync = useMutation({
    mutationFn: () => apiPost<MailboxSyncResult>('/communications/inbound/mailbox/sync', {}),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: KEY });
      qc.invalidateQueries({ queryKey: SETUP_QUERY_KEY });
      if (r.reason === 'busy') notify('info', 'El buzón ya se está revisando', 'Otra instancia lo está leyendo en este momento.');
      else if (r.reason === 'setup') notify('info', 'La entidad está en implementación', 'Los correos esperan en el buzón hasta pasar a operación.');
      else if (!r.ok) notify('error', 'La revisión falló', r.error);
      else notify('success', r.ingested ? `${r.ingested} correo(s) recibidos` : 'Sin correos nuevos', r.skipped ? `${r.skipped} ya estaban en la plataforma.` : undefined);
    },
    onError: notifyError,
  });

  async function readKeyFile(files: File[]) {
    setKeyFiles(files.slice(-1));
    setKeyError(null);
    const file = files.at(-1);
    if (!file) return;
    try {
      const j = await readServiceAccountJson(file);
      set({ serviceAccountEmail: j.clientEmail, secret: j.privateKey, clearSecret: false, keyFile: file.name });
    } catch (e) {
      setKeyError((e as Error).message || 'No se pudo leer el archivo.');
    }
  }

  const title = 'Buzón de correo entrante';
  if (q.isLoading) return <Card title={title}><LoadingState rows={3} /></Card>;
  if (q.isError || !v) return <Card title={title}><ErrorState error={q.error} onRetry={() => q.refetch()} /></Card>;
  const blocked = v.secretsKey === 'missing';
  const preset = IMAP_PRESETS[f.preset];
  const sync$ = v.sync;
  const failing = !!sync$?.lastError && (!sync$.lastSuccessAt || (sync$.lastErrorAt ?? '') > sync$.lastSuccessAt);
  const cancelEdit = () => { setF(initialForm(saved)); setKeyFiles([]); setKeyError(null); test.reset(); setEditing(false); };

  const syncStatus = v.source === 'tenant' && sync$ && (
    <div className="rounded-md border border-border p-3 text-sm">
      <KeyValue items={[
        { label: 'Última revisión', value: sync$.lastSyncAt ? fmtDate(sync$.lastSyncAt) : 'Todavía no se ha revisado' },
        { label: 'Correos recibidos', value: <span className="tnum">{sync$.receivedCount}</span> },
        { label: 'Ignorados', value: <span className="tnum" title="Respuestas automáticas, rebotes y correos de la propia entidad">{sync$.ignoredCount}</span> },
      ]} />
      {failing && <Alert variant="destructive" className="mt-3"><Icono nombre="mdi:alert-circle-outline" /><AlertTitle>La última lectura falló{sync$.lastErrorAt ? ` (${fmtDate(sync$.lastErrorAt)})` : ''}</AlertTitle><AlertDescription className="break-words">{sync$.lastError}</AlertDescription></Alert>}
    </div>
  );
  const testResult = (<>
    {test.data && (test.data.ok
      ? <Alert variant="success"><Icono nombre="mdi:check-circle" /><AlertTitle>La conexión funciona</AlertTitle><AlertDescription>{test.data.detail}</AlertDescription></Alert>
      : <Alert variant="destructive"><Icono nombre="mdi:alert-circle-outline" /><AlertTitle>No funcionó</AlertTitle><AlertDescription className="break-words">{test.data.error}</AlertDescription></Alert>)}
    {test.isError && <Alert variant="destructive"><AlertDescription>{errorMessage(test.error)}</AlertDescription></Alert>}
  </>);
  const savedAt = v.source === 'tenant' && v.updatedAt && <span className="text-xs text-muted-foreground ml-auto">Guardado {fmtDate(v.updatedAt)}{v.updatedBy ? ` · ${v.updatedBy}` : ''}</span>;
  const syncButton = v.source === 'tenant' && <Button type="button" size="sm" variant="outline" disabled={sync.isPending || !!sync$?.running} onClick={() => sync.mutate()}>{sync.isPending ? <Spinner /> : <Icono nombre="mdi:email-sync-outline" />} Revisar ahora</Button>;
  const removeButton = v.source === 'tenant' && <Button type="button" size="sm" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => setRemoving(true)}><Icono nombre="mdi:trash-can-outline" /> Quitar</Button>;
  const removeDialog = (
    <ConfirmarDialogo abierto={removing} onAbiertoChange={setRemoving} destructivo titulo="Quitar el buzón de correo entrante" textoConfirmar="Quitar"
      descripcion="La plataforma dejará de leer el buzón: las solicitudes que lleguen por correo no entrarán solas. Los correos ya recibidos se conservan en sus expedientes."
      onConfirmar={() => remove.mutateAsync().then(() => undefined)} />
  );

  if (summary && saved) {
    const providerIcon = PROVIDERS.find((p) => p.value === saved.provider)?.icon ?? 'mdi:email-outline';
    const interval = INTERVAL_OPTIONS.find((o) => o.value === String(saved.pollIntervalSeconds))?.label ?? `Cada ${saved.pollIntervalSeconds} s`;
    const access = saved.provider === 'IMAP'
      ? { label: 'Servidor', value: `${saved.host}:${saved.port} · ${saved.user}` }
      : saved.credentials === 'outbound'
        ? { label: 'Credenciales', value: saved.provider === 'MICROSOFT_365' ? 'La misma aplicación del correo saliente' : 'La misma cuenta de servicio del correo saliente' }
        : saved.provider === 'MICROSOFT_365'
          ? { label: 'Aplicación', value: saved.clientId ?? '—' }
          : { label: 'Cuenta de servicio', value: saved.serviceAccountEmail ?? '—' };
    return (
      <Card title={title} actions={sync$?.running ? <Badge variant="info-light" size="sm"><Spinner /> Revisando…</Badge> : undefined}>
        <ConfiguredService icon={providerIcon} title={<>{saved.mailbox} en {MAILBOX_PROVIDER_LABEL[saved.provider]}</>}
          items={[
            access,
            ...(saved.provider === 'IMAP' ? [{ label: 'Carpeta que se lee', value: saved.folder }] : []),
            { label: 'Lo procesado', value: saved.processedFolder ? `Se mueve a «${saved.processedFolder}»` : 'Se marca como leído' },
            { label: 'Se revisa', value: interval },
          ]}
          actions={<>
            <Button type="button" size="sm" variant="outline" onClick={() => { test.reset(); setEditing(true); }}><Icono nombre="mdi:pencil-outline" /> Editar</Button>
            <Button type="button" size="sm" variant="outline" disabled={test.isPending} onClick={() => test.mutate()}>{test.isPending ? <Spinner /> : <Icono nombre="mdi:lan-connect" />} Probar conexión</Button>
            {syncButton}
            {removeButton}
            {savedAt}
          </>}>
          {syncStatus}
          {testResult}
        </ConfiguredService>
        {removeDialog}
      </Card>
    );
  }

  return (
    <Card title={title} actions={v.source === 'tenant' && sync$?.running ? <Badge variant="info-light" size="sm"><Spinner /> Revisando…</Badge> : undefined}>
      <form className="flex flex-col gap-4" onSubmit={(e) => { e.preventDefault(); if (!missing) save.mutate(); }}>
        <p className="text-sm text-muted-foreground m-0">La plataforma lee este buzón y radica cada correo que llega (o lo agrega al caso al que responde). Nunca borra nada: lo leído se marca como leído o se mueve a la carpeta que indique.</p>
        {blocked && <Alert variant="destructive"><Icono nombre="mdi:key-outline" /><AlertTitle>El servidor no tiene SECRETS_KEY</AlertTitle><AlertDescription>Sin la llave maestra no se pueden guardar contraseñas. Hay que configurarla en el servidor.</AlertDescription></Alert>}
        {v.source === 'none' && <Alert variant="info"><Icono nombre="mdi:email-off-outline" /><AlertTitle>No se lee ningún buzón</AlertTitle><AlertDescription>Las solicitudes que los ciudadanos envían por correo no llegan solas a la plataforma hasta conectar el buzón.</AlertDescription></Alert>}

        <ProviderRadio idPrefix="im-provider" ariaLabel="Servicio del buzón" value={f.provider} options={PROVIDERS} onChange={(p) => set({ provider: p, secret: '', clearSecret: false, keyFile: null, useOutbound: false })} />

        {f.provider !== 'IMAP' && canUseOutbound && (
          <div className="flex items-center gap-2">
            <Switch id="im-outbound" checked={f.useOutbound} onCheckedChange={(c) => set({ useOutbound: !!c, secret: '', clearSecret: false, keyFile: null })} />
            <Label htmlFor="im-outbound" className="font-normal">Usar {f.provider === 'MICROSOFT_365' ? 'la misma aplicación' : 'la misma cuenta de servicio'} del correo saliente</Label>
          </div>
        )}
        {f.useOutbound && f.provider === 'MICROSOFT_365' && <Alert variant="info"><Icono nombre="mdi:information-outline" /><AlertDescription>Agregue a esa aplicación el permiso de aplicación Microsoft Graph › Mail.ReadWrite (con consentimiento del administrador). Si la limitó a un buzón con RBAC, incluya este.</AlertDescription></Alert>}
        {f.useOutbound && f.provider === 'GOOGLE_WORKSPACE' && <Alert variant="info"><Icono nombre="mdi:information-outline" /><AlertDescription>Agregue a la delegación de todo el dominio de esa cuenta de servicio el permiso https://www.googleapis.com/auth/gmail.modify.</AlertDescription></Alert>}

        {f.provider === 'MICROSOFT_365' && !f.useOutbound && (<>
          <Plegable titulo="Cómo obtener estos datos">
            <ol className="text-sm text-muted-foreground m-0 pl-5 space-y-1">
              <li>En el centro de administración de Microsoft Entra, abra Registros de aplicaciones y cree un registro nuevo (solo cuentas de este directorio), o use el del correo saliente.</li>
              <li>En Permisos de API agregue Microsoft Graph › Permisos de aplicación › Mail.ReadWrite y conceda el consentimiento de administrador. Sirve para leer, marcar como leído y mover; no para borrar buzones.</li>
              <li>En Certificados y secretos cree un secreto de cliente y copie su valor. Anote la fecha en que vence: ese día se debe reemplazar aquí.</li>
              <li>Recomendado: limite la aplicación al buzón de solicitudes en Exchange Online (RBAC para aplicaciones). Sin eso puede leer cualquier buzón de la entidad.</li>
            </ol>
          </Plegable>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Id del directorio (inquilino)" htmlFor="im-tenant"><Input id="im-tenant" autoComplete="off" placeholder="entidad.onmicrosoft.com o GUID" value={f.tenantId} onChange={(e) => set({ tenantId: e.target.value })} /></Field>
            <Field label="Id de la aplicación (cliente)" htmlFor="im-client"><Input id="im-client" autoComplete="off" placeholder="00000000-0000-0000-0000-000000000000" value={f.clientId} onChange={(e) => set({ clientId: e.target.value })} /></Field>
            <SecretField id="im-secret" label={SECRET_LABEL[f.provider]} value={f.secret} onChange={(secret) => set({ secret })} stored={storedSecretApplies} />
          </div>
        </>)}

        {f.provider === 'GOOGLE_WORKSPACE' && !f.useOutbound && (<>
          <Plegable titulo="Cómo obtener estos datos">
            <ol className="text-sm text-muted-foreground m-0 pl-5 space-y-1">
              <li>En Google Cloud, en un proyecto de la entidad, habilite la API de Gmail y cree una cuenta de servicio (o use la del correo saliente). En Claves agregue una clave nueva de tipo JSON.</li>
              <li>En la Consola de administración de Google Workspace › Seguridad › Controles de API › Delegación de todo el dominio, agregue el id de cliente de la cuenta de servicio con el permiso https://www.googleapis.com/auth/gmail.modify (leer, marcar y etiquetar; no borrar ni enviar).</li>
              <li>Cargue aquí el JSON e indique el buzón que se lee.</li>
            </ol>
          </Plegable>
          <CargaArchivos archivos={keyFiles} onArchivosChange={readKeyFile} accept=".json,application/json" maxTamano={64 * 1024} textoAyuda={storedSecretApplies && !f.keyFile ? 'Hay una llave guardada; cargue otro JSON solo para cambiarla.' : 'JSON de la cuenta de servicio. Se guarda solo la llave, cifrada.'} aria-label="Cargar el JSON de la cuenta de servicio" />
          {keyError && <Alert variant="destructive"><AlertDescription>{keyError}</AlertDescription></Alert>}
          <Field label="Cuenta de servicio" htmlFor="im-sa" hint={f.keyFile ? `Llave tomada de ${f.keyFile}.` : undefined}><Input id="im-sa" type="email" autoComplete="off" value={f.serviceAccountEmail} onChange={(e) => set({ serviceAccountEmail: e.target.value })} /></Field>
          {storedSecretApplies && !f.keyFile && <div className="flex items-center gap-2"><Checkbox id="im-clear" checked={f.clearSecret} onCheckedChange={(c) => set({ clearSecret: !!c })} /><Label htmlFor="im-clear" className="font-normal">Quitar la llave guardada</Label></div>}
        </>)}

        {f.provider === 'IMAP' && (<>
          <Plegable titulo="Cómo obtener estos datos">
            <ol className="text-sm text-muted-foreground m-0 pl-5 space-y-1">
              <li>Elija el servicio: la plantilla llena el servidor, el puerto y la seguridad. Para otro servidor, habilite el acceso IMAP del buzón y pida los datos a quien administra el correo (993 con TLS es lo habitual).</li>
              <li>Una cuenta normal de Gmail sirve con una contraseña de aplicación. Google ya no la muestra en el menú: con la cuenta abierta, entre a myaccount.google.com/apppasswords (o busque «contraseñas de aplicación» en myaccount.google.com), escriba un nombre como «SGDEA» y copie las 16 letras sin espacios. Si dice que no está disponible, active antes la verificación en dos pasos. La plataforma no la muestra nunca.</li>
              <li>Google Workspace y Microsoft 365 no aceptan la contraseña de la cuenta por IMAP: use su opción propia. Outlook.com personal tampoco acepta IMAP con contraseña.</li>
            </ol>
          </Plegable>
          <Field label="Servicio" htmlFor="im-preset" hint={preset.hint}>
            <SelectorSimple id="im-preset" value={f.preset} options={Object.entries(IMAP_PRESETS).map(([value, p]) => ({ value, label: p.label }))}
              onChange={(k) => { if (!k) return; const p = IMAP_PRESETS[k as ImapPresetKey]; set({ preset: k as ImapPresetKey, ...(k === 'custom' ? {} : { host: p.host, port: String(p.port), security: p.security }) }); }} />
          </Field>
          <div className="grid sm:grid-cols-[1fr_8rem] gap-3">
            <Field label="Servidor" htmlFor="im-host"><Input id="im-host" autoComplete="off" placeholder="imap.entidad.gov.co" value={f.host} onChange={(e) => set({ host: e.target.value })} /></Field>
            <Field label="Puerto" htmlFor="im-port"><Input id="im-port" className="tnum" type="number" inputMode="numeric" min={1} max={65535} value={f.port} onChange={(e) => set({ port: e.target.value })} /></Field>
          </div>
          <Field label="Seguridad" htmlFor="im-security"><SelectorSimple id="im-security" value={f.security} options={SECURITY_OPTIONS} onChange={(s) => s && set({ security: s as ImapSecurity })} /></Field>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Usuario" htmlFor="im-user"><Input id="im-user" autoComplete="off" value={f.user} onChange={(e) => set({ user: e.target.value })} /></Field>
            <SecretField id="im-secret" label={SECRET_LABEL[f.provider]} value={f.secret} onChange={(secret) => set({ secret })} stored={storedSecretApplies} />
            <Field label="Carpeta que se lee" htmlFor="im-folder" hint="Normalmente INBOX."><Input id="im-folder" autoComplete="off" value={f.folder} onChange={(e) => set({ folder: e.target.value })} /></Field>
          </div>
        </>)}

        <div className="grid sm:grid-cols-3 gap-3">
          <Field label="Buzón" htmlFor="im-mailbox" required hint="La dirección a la que escriben los ciudadanos, por ejemplo pqrs@entidad.gov.co. Es la dirección de recepción de la entidad."><Input id="im-mailbox" type="email" autoComplete="off" value={f.mailbox} onChange={(e) => set({ mailbox: e.target.value })} /></Field>
          <Field label="Carpeta para lo procesado" htmlFor="im-processed" hint="Vacío: solo se marca como leído. Nunca se borra nada."><Input id="im-processed" autoComplete="off" placeholder="Solicitudes procesadas" value={f.processedFolder} onChange={(e) => set({ processedFolder: e.target.value })} /></Field>
          <Field label="Revisar" htmlFor="im-interval"><SelectorSimple id="im-interval" value={f.pollIntervalSeconds} options={INTERVAL_OPTIONS} onChange={(s) => s && set({ pollIntervalSeconds: s })} /></Field>
        </div>

        {testResult}
        {missing && <p className="text-xs text-muted-foreground m-0">Falta {missing}.</p>}

        <div className="flex flex-wrap items-center gap-2">
          <Button type="submit" size="sm" disabled={!!missing || save.isPending || blocked}>{save.isPending ? <Spinner /> : <Icono nombre="mdi:check" />} Guardar</Button>
          {editing && <Button type="button" size="sm" variant="ghost" onClick={cancelEdit}>Cancelar</Button>}
          <Button type="button" size="sm" variant="outline" disabled={!!missing || test.isPending} onClick={() => test.mutate()}>{test.isPending ? <Spinner /> : <Icono nombre="mdi:lan-connect" />} Probar conexión</Button>
          {syncButton}
          {removeButton}
          {savedAt}
        </div>
      </form>
      {removeDialog}
    </Card>
  );
}
